import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { verifyAuth } from "../../authverify";
import ConversationalAssistant from "../../component/ConversationalAssistant/ConversationalAssistant";
import { content } from "../../component/ConversationalAssistant/conversationEngine";
import { getCreativeHtml } from "../../component/ConversationalAssistant/creativeAssets";
import "./EndemicDemo.css";

const ARTICLES = [
  {
    type: "Review",
    specialty: "Nephrology",
    title:
      "ADPKD progression: reading the Mayo imaging class and the PROPKD score",
    description:
      "Two tools that separate rapid from slow progressors, and what each one needs.",
    readTime: "12 min read",
    gradient: "linear-gradient(135deg,#5E60F3,#1D97FA)",
  },
  {
    type: "Update",
    specialty: "Hematology",
    title:
      "Myelofibrosis after JAK inhibitor failure: switch, trial or transplant?",
    description:
      "How blood counts and symptoms steer the next line of therapy.",
    readTime: "9 min read",
    gradient: "linear-gradient(135deg,#B4232F,#E2525C)",
  },
  {
    type: "Perspective",
    specialty: "Dermatology",
    title: "Itch is what patients with psoriasis want treated first",
    description:
      "Topical options that ease itch and scale, from steroids to newer nonsteroidals.",
    readTime: "6 min read",
    gradient: "linear-gradient(135deg,#742594,#B04FC9)",
  },
  {
    type: "Pearls",
    specialty: "Diabetes",
    title: "Individualizing A1C goals: when 7% isn't the target",
    description:
      "Matching glycemic goals, and dose steps, to each patient.",
    readTime: "5 min read",
    gradient: "linear-gradient(135deg,#B45309,#EE7D30)",
  },
  {
    type: "Guide",
    specialty: "Rheumatology",
    title: "Knee osteoarthritis pain: what works, and what works fast",
    description:
      "Topical NSAIDs, injections and exercise, ranked by the evidence.",
    readTime: "7 min read",
    gradient: "linear-gradient(135deg,#0C56A6,#3B82C4)",
  },
];

const EndemicDemo = () => {
  const history = useHistory();
  const [agentOpen, setAgentOpen] = useState(true);
  const [showAdDisclosure, setShowAdDisclosure] = useState(false);
  const [notice, setNotice] = useState(null);
  const noticeTimer = useRef(null);
  const display = content.display.webRail;
  const displayHtml = getCreativeHtml(display.asset);

  useEffect(() => {
    if (!verifyAuth()) {
      history.replace("/");
    }
  }, [history]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = content.envs.web.title;

    return () => {
      document.title = previousTitle || "Partner Toolbox";
      clearTimeout(noticeTimer.current);
    };
  }, []);

  const showDestination = () => {
    setNotice({
      title: `Opens https://${display.url}`,
      body: `In a new tab. Standard display · ${display.w} × ${display.h}.`,
    });
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 3600);
  };

  return (
    <main
      className={`ca-scope endemic-demo ${
        agentOpen ? "agent-open" : "agent-closed"
      }`}
    >
      <div className="p-page">
        <div className="p-util">
          <div className="p-wrap">
            <span>Journals</span>
            <span>CME</span>
            <span>Newsletters</span>
            <span>Global Editions ▾</span>
          </div>
        </div>

        <header className="p-nav">
          <div className="p-wrap">
            <div className="p-logo">
              <span className="p-tile">+</span>
              <div>
                <b>Rounds Review</b>
                <small>EVIDENCE FOR PRACTICING CLINICIANS</small>
              </div>
            </div>
            <nav className="p-links" aria-label="Publisher navigation">
              <span>News</span>
              <span>Specialties</span>
              <span>Guidelines</span>
              <span>Journals</span>
              <span>CME</span>
              <span>Podcasts</span>
            </nav>
            <span className="p-search" aria-label="Search">
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="10.5" cy="10.5" r="6.5" />
                <path d="M15.5 15.5L21 21" />
              </svg>
            </span>
            <span className="p-acct">Dr. T. Collins</span>
          </div>
        </header>

        <section className="p-hero">
          <div className="p-wrap">
            <h1>Spotting the fast progressors in ADPKD</h1>
            <p>
              Kidney volume for age, the eGFR trend and genotype now decide
              who needs disease-modifying therapy. Our editors review the 2025
              KDIGO guideline.
            </p>
            <span className="p-cta">Read the review →</span>
          </div>
        </section>

        <div className="p-latest">
          <div className="p-wrap">
            <span className="p-tag">LATEST</span>
            <span>
              Editors&apos; roundup: the options when myelofibrosis stops
              responding to a JAK inhibitor.
            </span>
            <span className="p-latest-link">Read the analysis →</span>
          </div>
        </div>

        <section className="p-sec">
          <div className="p-wrap">
            <div className="p-grid">
              <div className="p-list">
                <h3>Latest across specialties</h3>
                {ARTICLES.map((article) => (
                  <article className="p-art" key={article.title}>
                    <div
                      className="p-th"
                      style={{ background: article.gradient }}
                    >
                      {article.type}
                    </div>
                    <div>
                      <div className="k">{article.specialty}</div>
                      <h4>{article.title}</h4>
                      <p>{article.description}</p>
                      <div className="mt">{article.readTime}</div>
                    </div>
                  </article>
                ))}
              </div>

              <aside className="p-rail" aria-label="Advertisement">
                <div className="std-slot">
                  <div className="std-lab">
                    <span>Advertisement</span>
                    <button
                      type="button"
                      className="why"
                      aria-label="About this advertisement"
                      title="About this advertisement"
                      onClick={() =>
                        setShowAdDisclosure((current) => !current)
                      }
                    >
                      i
                    </button>
                  </div>
                  {showAdDisclosure && (
                    <div className="endemic-ad-disclosure" role="status">
                      Standard display · 300 × 250. Served to a verified HCP
                      (NPI matched) on an endemic publisher page. Creative
                      PRM-MR-01, MLR-approved.
                    </div>
                  )}
                  <div
                    className="std endemic-standard-ad"
                    style={{ width: display.w, height: display.h }}
                    role="button"
                    tabIndex={0}
                    onClick={showDestination}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        showDestination();
                      }
                    }}
                    aria-label={display.alt}
                  >
                    <iframe
                      title="Primera advertisement"
                      srcDoc={displayHtml}
                      width={display.w}
                      height={display.h}
                      tabIndex="-1"
                    />
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <footer className="p-foot">
          <div className="p-wrap">
            <b>Rounds Review</b>
            <span>About</span>
            <span>Editorial policy</span>
            <span>Advertise</span>
            <span>Privacy</span>
            <span>Terms</span>
            <span>© 2026 Rounds Review</span>
          </div>
        </footer>
      </div>

      <ConversationalAssistant
        environment="web"
        onOpenChange={setAgentOpen}
      />

      {notice && (
        <div className="endemic-notice" role="status">
          <b>{notice.title}</b>
          <span>{notice.body}</span>
        </div>
      )}
    </main>
  );
};

export default EndemicDemo;
